import { Capacitor } from '@capacitor/core'
import { ENV } from '../config/env.js'
import iapProductsData from '../data/iapProducts.json'

export type IAPProvider = 'mock' | 'capacitor' | 'revenuecat' | 'web'

export interface IAPProduct {
  id: string
  priceDisplay: string
  available: boolean
  type: 'consumable' | 'subscription'
}

/**
 * Resolves the correct RevenueCat API key for the current platform.
 * iOS uses VITE_REVENUECAT_API_KEY_IOS, Android uses VITE_REVENUECAT_API_KEY_ANDROID.
 * Returns an empty string on web (RevenueCat is not used there).
 */
function resolveRevenueCatApiKey(): string {
  const platform = Capacitor.getPlatform()
  if (platform === 'ios') return ENV.REVENUECAT_API_KEY_IOS
  if (platform === 'android') return ENV.REVENUECAT_API_KEY_ANDROID
  return ''
}

export class IAPService {
  private provider: IAPProvider = 'mock'
  private static _instance: IAPService | null = null

  private constructor() {}

  static getInstance(): IAPService {
    if (!IAPService._instance) {
      IAPService._instance = new IAPService()
    }
    return IAPService._instance
  }

  /**
   * Initialise the IAP service with the chosen provider.
   *
   * 'revenuecat' — uses @revenuecat/purchases-capacitor.
   *   On native platforms: calls Purchases.configure() with the API key from ENV.
   *   On web (browser): gracefully falls back to 'mock' — RevenueCat requires native billing.
   *
   * NOTE: Requires `VITE_REVENUECAT_API_KEY_IOS` and `VITE_REVENUECAT_API_KEY_ANDROID`
   * to be set in .env before App Store submission.
   * See PHASE_4_IAP_README.md for full setup instructions.
   */
  static async init(provider: IAPProvider = 'mock'): Promise<IAPService> {
    const instance = IAPService.getInstance()

    if (provider === 'revenuecat') {
      if (!Capacitor.isNativePlatform()) {
        // RevenueCat SDK requires native iOS/Android billing. Fall back gracefully on web.
        console.warn(
          '[IAPService] RevenueCat requires a native platform. Falling back to mock on web.',
        )
        instance.provider = 'mock'
        return instance
      }

      const apiKey = resolveRevenueCatApiKey()
      if (!apiKey) {
        // TODO: Owner must set VITE_REVENUECAT_API_KEY_IOS / VITE_REVENUECAT_API_KEY_ANDROID
        // in .env before building for the App Store. Without a key, RevenueCat cannot initialise.
        console.warn(
          '[IAPService] RevenueCat API key is empty. ' +
            'Set VITE_REVENUECAT_API_KEY_IOS / VITE_REVENUECAT_API_KEY_ANDROID in .env. ' +
            'Falling back to mock.',
        )
        instance.provider = 'mock'
        return instance
      }

      try {
        const { Purchases } = await import('@revenuecat/purchases-capacitor')
        await Purchases.configure({ apiKey })
        instance.provider = 'revenuecat'
        console.log('[IAPService] RevenueCat configured successfully on', Capacitor.getPlatform())
      } catch (err) {
        console.error('[IAPService] RevenueCat configure failed:', err)
        instance.provider = 'mock'
      }

      return instance
    }

    instance.provider = provider
    return instance
  }

  /**
   * Returns product metadata for the given product IDs.
   *
   * 'revenuecat': fetches all offerings from RevenueCat and maps available packages to IAPProduct.
   *   Products are matched by their store identifier (appleProductId / googleProductId).
   *   Falls back to mock data for any ID not found in offerings.
   * 'mock': returns entries from iapProducts.json with available=false.
   */
  async loadProducts(productIds: string[]): Promise<IAPProduct[]> {
    if (this.provider === 'mock') {
      return productIds.map((id) => {
        const found = iapProductsData.products.find((p) => p.id === id)
        return {
          id,
          priceDisplay: found?.priceDisplay ?? '—',
          available: false,
          type: (found?.type as 'consumable' | 'subscription') ?? 'consumable',
        }
      })
    }

    if (this.provider === 'revenuecat') {
      return this.loadProductsRevenueCat(productIds)
    }

    if (this.provider === 'capacitor') {
      // Phase 4.5: implement with @capacitor-community/in-app-purchases
      // Example:
      // const { InAppPurchase2 } = await import('@ionic-native/in-app-purchases-2')
      // return productIds.map(id => {
      //   const product = InAppPurchase2.get(id)
      //   return { id, priceDisplay: product.price, available: product.valid, type: product.type }
      // })
      throw new Error('IAPService: capacitor provider not yet implemented. See PHASE_4_IAP_README.md.')
    }

    // web / future provider
    throw new Error(`IAPService: provider '${this.provider}' not yet implemented. See PHASE_4_IAP_README.md.`)
  }

  /**
   * Attempt a purchase.
   * 'revenuecat': finds the matching RevenueCat package and calls Purchases.purchasePackage().
   * 'mock': always returns a user-friendly failure.
   */
  async purchase(productId: string): Promise<{ success: boolean; error?: string }> {
    if (this.provider === 'mock') {
      return {
        success: false,
        error: 'In-App-Käufe werden in der nächsten Version verfügbar sein.',
      }
    }

    if (this.provider === 'capacitor') {
      return this.purchaseCapacitor(productId)
    }

    if (this.provider === 'revenuecat') {
      return this.purchaseRevenueCat(productId)
    }

    return {
      success: false,
      error: `IAPService: provider '${this.provider}' not yet implemented. See PHASE_4_IAP_README.md.`,
    }
  }

  /**
   * Restore non-consumable / subscription purchases on a new device.
   * 'revenuecat': calls Purchases.restorePurchases().
   */
  async restorePurchases(): Promise<void> {
    if (this.provider === 'mock') return

    if (this.provider === 'revenuecat') {
      try {
        const { Purchases } = await import('@revenuecat/purchases-capacitor')
        await Purchases.restorePurchases()
        console.log('[IAPService] Purchases restored via RevenueCat.')
      } catch (err) {
        console.error('[IAPService] restorePurchases failed:', err)
      }
      return
    }

    if (this.provider === 'capacitor') {
      // Phase 4.5: InAppPurchase2.refresh()
      return
    }
  }

  /**
   * Returns true only when a real payment provider is configured.
   * Use this to conditionally show lock overlays and "coming soon" labels.
   */
  isRealPurchaseEnabled(): boolean {
    return this.provider !== 'mock'
  }

  getProvider(): IAPProvider {
    return this.provider
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Loads all RevenueCat offerings and maps them to IAPProduct for the requested IDs.
   * RevenueCat product identifiers are matched against iapProducts.json store IDs
   * (appleProductId on iOS, googleProductId on Android).
   */
  private async loadProductsRevenueCat(productIds: string[]): Promise<IAPProduct[]> {
    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor')
      const offerings = await Purchases.getOfferings()

      // Collect all packages across all offerings for a flat lookup table.
      const allPackages = Object.values(offerings.all).flatMap((o) => o.availablePackages)

      const platform = Capacitor.getPlatform()

      return productIds.map((id) => {
        // Resolve the platform-specific store identifier from our catalog.
        const catalogEntry = iapProductsData.products.find((p) => p.id === id)
        const storeId =
          platform === 'ios'
            ? (catalogEntry as { appleProductId?: string } | undefined)?.appleProductId
            : (catalogEntry as { googleProductId?: string } | undefined)?.googleProductId

        // Find the matching RevenueCat package by store product identifier.
        const pkg = storeId
          ? allPackages.find((p) => p.product.identifier === storeId)
          : undefined

        if (pkg) {
          const productType = pkg.product.productCategory === 'SUBSCRIPTION' ? 'subscription' : 'consumable'
          return {
            id,
            priceDisplay: pkg.product.priceString,
            available: true,
            type: productType as 'consumable' | 'subscription',
          }
        }

        // Fallback to catalog data when not found in RevenueCat offerings.
        return {
          id,
          priceDisplay: catalogEntry?.priceDisplay ?? '—',
          available: false,
          type: (catalogEntry?.type as 'consumable' | 'subscription') ?? 'consumable',
        }
      })
    } catch (err) {
      console.error('[IAPService] loadProductsRevenueCat failed:', err)
      // Return unavailable products so the UI degrades gracefully.
      return productIds.map((id) => {
        const found = iapProductsData.products.find((p) => p.id === id)
        return {
          id,
          priceDisplay: found?.priceDisplay ?? '—',
          available: false,
          type: (found?.type as 'consumable' | 'subscription') ?? 'consumable',
        }
      })
    }
  }

  /**
   * RevenueCat purchase flow:
   * 1. Fetch all offerings to find the package matching the productId.
   * 2. Call Purchases.purchasePackage() with the resolved package.
   * 3. Return success if the customer has at least one active entitlement after purchase.
   */
  private async purchaseRevenueCat(productId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { Purchases } = await import('@revenuecat/purchases-capacitor')

      const offerings = await Purchases.getOfferings()
      const allPackages = Object.values(offerings.all).flatMap((o) => o.availablePackages)

      const platform = Capacitor.getPlatform()
      const catalogEntry = iapProductsData.products.find((p) => p.id === productId)
      const storeId =
        platform === 'ios'
          ? (catalogEntry as { appleProductId?: string } | undefined)?.appleProductId
          : (catalogEntry as { googleProductId?: string } | undefined)?.googleProductId

      const pkg = storeId
        ? allPackages.find((p) => p.product.identifier === storeId)
        : undefined

      if (!pkg) {
        return {
          success: false,
          error: `Produkt "${productId}" wurde in RevenueCat-Angeboten nicht gefunden.`,
        }
      }

      const result = await Purchases.purchasePackage({ aPackage: pkg })
      const hasActiveEntitlement =
        Object.values(result.customerInfo.entitlements.active).length > 0 ||
        // Consumables don't create entitlements — treat a completed transaction as success.
        result.transaction != null

      return { success: hasActiveEntitlement }
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err)
      // RevenueCat throws a specific error when the user cancels the purchase dialog.
      if (message.includes('UserCancelledError') || message.includes('1')) {
        return { success: false, error: 'Kauf abgebrochen.' }
      }
      console.error('[IAPService] purchaseRevenueCat failed:', err)
      return { success: false, error: message }
    }
  }

  /**
   * Phase 4.5: Capacitor native billing via @capacitor-community/in-app-purchases.
   * See PHASE_4_IAP_README.md for full setup instructions.
   */
  private async purchaseCapacitor(productId: string): Promise<{ success: boolean; error?: string }> {
    // Phase 4.5: implement with @capacitor-community/in-app-purchases
    // import { InAppPurchase2 } from '@ionic-native/in-app-purchases-2'
    // const product = store.get(productId)
    // return new Promise(resolve => {
    //   store.order(productId)
    //   store.when(productId).approved(p => { p.finish(); resolve({ success: true }) })
    //   store.when(productId).error(() => resolve({ success: false, error: 'Purchase failed' }))
    // })
    void productId
    return { success: false, error: 'IAP provider not yet configured. See PHASE_4_IAP_README.md.' }
  }
}
